import React from 'react';
import { motion } from 'framer-motion';
import { Globe, RefreshCw, ExternalLink, MoreVertical } from 'lucide-react';
import type { DNSZone } from '../../../types/dns';

interface ZoneHeaderProps {
  zone: DNSZone;
  isRefreshing: boolean;
  onRefresh: () => void;
}

const ZoneHeader: React.FC<ZoneHeaderProps> = ({ zone, isRefreshing, onRefresh }) => {
  const getProviderIcon = (providerType: string | undefined) => {
    switch (providerType?.toLowerCase()) {
      case 'cloudflare':
        return {
          icon: '🔶',
          color: 'text-orange-500',
          bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        };
      case 'route53':
      case 'aws':
        return {
          icon: '🟠',
          color: 'text-orange-600',
          bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        };
      case 'azure':
        return { 
          icon: '🔵', 
          color: 'text-blue-500', 
          bgColor: 'bg-blue-100 dark:bg-blue-900/30' 
        };
      case 'gcp':
      case 'google':
        return {
          icon: '🟢',
          color: 'text-green-500',
          bgColor: 'bg-green-100 dark:bg-green-900/30',
        };
      case 'digitalocean':
        return { 
          icon: '🌊', 
          color: 'text-cyan-500', 
          bgColor: 'bg-cyan-100 dark:bg-cyan-900/30' 
        };
      default:
        return { 
          icon: '🛡️', 
          color: 'text-gray-500', 
          bgColor: 'bg-gray-100 dark:bg-gray-700' 
        };
    }
  };

  const providerInfo = getProviderIcon(zone.providerType);

  return (
    <div className="card-header">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-heading-1 text-slate-900 dark:text-white">
              {zone.name}
            </h2>
            <div className="flex items-center space-x-2 mt-1">
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium ${providerInfo.bgColor} ${providerInfo.color}`}>
                <span className="text-sm">{providerInfo.icon}</span>
                <span>{zone.providerName}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <motion.button
            className="inline-flex items-center justify-center p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded-lg transition-colors focus:outline-none"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </motion.button>
          <motion.button
            className="inline-flex items-center justify-center p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded-lg transition-colors focus:outline-none"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ExternalLink className="w-4 h-4" />
          </motion.button>
          <motion.button
            className="inline-flex items-center justify-center p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded-lg transition-colors focus:outline-none"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <MoreVertical className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default ZoneHeader;